import { createFileRoute, Link } from "@tanstack/react-router";
import { useTheme } from "../../components/ThemeProvider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";

export const Route = createFileRoute("/theme-demo/")({
  component: ThemeDemoPage,
});

function ThemeDemoPage() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="container mx-auto max-w-4xl space-y-8">
        <div className="flex flex-col gap-4">
          <Link
            to="/"
            className="inline-flex items-center text-primary hover:text-primary/80 transition-colors"
          >
            ← Back to Home
          </Link>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-foreground mb-2">
              Theme &amp; UI Demo
            </h1>
            <p className="text-lg text-muted-foreground">
              CSS variables and light/dark/system theme switcher
            </p>
          </div>
        </div>

        {/* Theme switcher */}
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">Theme</CardTitle>
            <CardDescription className="text-muted-foreground">
              Switch between light, dark, and system. Uses ThemeProvider.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {(["light", "dark", "system"] as const).map((t) => (
              <Button
                key={t}
                variant={theme === t ? "default" : "outline"}
                onClick={() => setTheme(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* CSS variable samples */}
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">Design tokens</CardTitle>
            <CardDescription className="text-muted-foreground">
              text-foreground, bg-background, primary, muted, card, etc.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-background border border-border">
                <p className="text-sm text-muted-foreground">bg-background</p>
                <p className="text-foreground font-medium">text-foreground</p>
              </div>
              <div className="p-4 rounded-lg bg-card border border-border text-card-foreground">
                <p className="text-sm text-muted-foreground">bg-card</p>
                <p className="font-medium">text-card-foreground</p>
              </div>
              <div className="p-4 rounded-lg bg-primary text-primary-foreground">
                <p className="text-sm opacity-90">bg-primary</p>
                <p className="font-medium">text-primary-foreground</p>
              </div>
              <div className="p-4 rounded-lg bg-muted text-muted-foreground">
                <p className="text-sm">bg-muted</p>
                <p className="font-medium">text-muted-foreground</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="default">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="link">Link</Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground pb-8">
          Theme demo — all tokens respect the current theme. SSR-safe.
        </p>
      </div>
    </div>
  );
}
